import { useEffect, useRef, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Clock, Sun, Cube, MagnifyingGlass, Path,
  Stack, Compass as CompassIcon, Waveform, Broadcast,
} from '@phosphor-icons/react';
import { safeGradient, safeRadius, safeAlpha, debounce } from '../utils/canvasSafe';

const IS_MOBILE = typeof navigator !== 'undefined' && (
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
  (navigator.maxTouchPoints > 1 && typeof window !== 'undefined' && window.innerWidth < 1024)
);
const IS_TABLET = typeof window !== 'undefined' && window.innerWidth >= 768 && window.innerWidth < 1280;

// Phase timings (ms)
const PHASE_1_REACT = 600;
const PHASE_2_EXPAND = 1100;
const PHASE_3_FORM = 1400;
const PHASE_5_RETURN = 700;

export function HolographicLocation({ isOpen, onClose, locationData }) {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const startTimeRef = useRef(0);
  const closingStartRef = useRef(0);
  const [phase, setPhase] = useState(0);   // 0..3: active phases. 4=closing
  const [closing, setClosing] = useState(false);
  const [actions] = useState({ vision3D: true, zoom: 1.25, satellite: false, route: false, layers: true });
  const [now, setNow] = useState(new Date());

  // Phase timing
  useEffect(() => {
    if (isOpen) {
      setPhase(0);
      setClosing(false);
      startTimeRef.current = performance.now();
      const t1 = setTimeout(() => setPhase(1), PHASE_1_REACT);
      const t2 = setTimeout(() => setPhase(2), PHASE_1_REACT + PHASE_2_EXPAND);
      const t3 = setTimeout(() => setPhase(3), PHASE_1_REACT + PHASE_2_EXPAND + PHASE_3_FORM);
      return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
    }
  }, [isOpen]);

  // Live clock
  useEffect(() => {
    if (!isOpen) return;
    const id = setInterval(() => setNow(new Date()), 30 * 1000);
    return () => clearInterval(id);
  }, [isOpen]);

  // Smooth close (phase 5)
  const handleClose = () => {
    if (closing) return;
    setClosing(true);
    closingStartRef.current = performance.now();
    setTimeout(onClose, PHASE_5_RETURN);
  };

  // Canvas render
  useEffect(() => {
    if (!isOpen || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    const sizeCanvas = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, IS_MOBILE ? 1.5 : 2);
      const w = canvas.clientWidth || window.innerWidth;
      const h = canvas.clientHeight || window.innerHeight;
      canvas.width = Math.max(1, Math.floor(w * dpr));
      canvas.height = Math.max(1, Math.floor(h * dpr));
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    sizeCanvas();
    const handleResize = debounce(sizeCanvas, 150);
    window.addEventListener('resize', handleResize);

    const PARTICLE_COUNT = IS_MOBILE ? 140 : IS_TABLET ? 220 : 360;
    const BUILDING_COUNT = IS_MOBILE ? 26 : IS_TABLET ? 42 : 70;
    const ROAD_LINES = IS_MOBILE ? 5 : 8;

    // Create isometric grid for city floor
    // Use isometric projection: angle ~30deg
    const ISO_ANGLE = Math.PI / 6;
    const COS_ISO = Math.cos(ISO_ANGLE);
    const SIN_ISO = Math.sin(ISO_ANGLE);
    const isoProject = (gx, gy, gz = 0) => {
      const x = (gx - gy) * COS_ISO;
      const y = (gx + gy) * SIN_ISO - gz;
      return [x, y];
    };

    // Particles
    const particles = Array.from({ length: PARTICLE_COUNT }, () => {
      const angle = Math.random() * Math.PI * 2;
      const dist = 10 + Math.random() * 80;
      const u = (Math.random() - 0.5) * 2;
      const v = (Math.random() - 0.5) * 2;
      return {
        angle, dist,
        gridU: u, gridV: v,
        size: 0.4 + Math.random() * 1.6,
        speed: 0.3 + Math.random() * 0.8,
        phase: Math.random() * Math.PI * 2,
        brightness: 0.35 + Math.random() * 0.65,
      };
    });

    // Buildings: cluster in central area, some sparse at edges
    const buildings = Array.from({ length: BUILDING_COUNT }, () => {
      // gridU, gridV in [-1.4, 1.4] range
      const r = Math.pow(Math.random(), 0.65); // weight toward center
      const ang = Math.random() * Math.PI * 2;
      return {
        gu: Math.cos(ang) * r * 1.5,
        gv: Math.sin(ang) * r * 1.5,
        w: 0.06 + Math.random() * 0.14,
        d: 0.06 + Math.random() * 0.14,
        h: 0.15 + Math.random() * 1.0,
        delay: Math.random() * 0.7,
        seed: Math.random(),
      };
    });

    // Roads (avenues)
    const roads = Array.from({ length: ROAD_LINES * 2 }, (_, i) => {
      const u = (i - ROAD_LINES) / ROAD_LINES;
      return { u };
    });

    let lastFrame = performance.now();

    const draw = () => {
      try {
        const now = performance.now();
        const dt = Math.min((now - lastFrame) / 1000, 0.05);
        lastFrame = now;
        const elapsed = (now - startTimeRef.current) / 1000;

        const cw = canvas.clientWidth || window.innerWidth;
        const ch = canvas.clientHeight || window.innerHeight;
        const cx = cw / 2;
        const cy = ch * 0.58;

        // Closing animation (phase 5: zoom out, particles return)
        const closingT = closing ? Math.min((now - closingStartRef.current) / PHASE_5_RETURN, 1) : 0;
        const reverse = closingT; // 0..1

        // Phase progress
        const tReact = Math.max(0, Math.min(elapsed / (PHASE_1_REACT / 1000), 1));
        const tExpand = Math.max(0, Math.min((elapsed - PHASE_1_REACT / 1000) / (PHASE_2_EXPAND / 1000), 1));
        const tForm = Math.max(0, Math.min((elapsed - (PHASE_1_REACT + PHASE_2_EXPAND) / 1000) / (PHASE_3_FORM / 1000), 1));
        const tStable = elapsed > (PHASE_1_REACT + PHASE_2_EXPAND + PHASE_3_FORM) / 1000 ? 1 : 0;

        // Effective form (decreases during closing)
        const effForm = Math.max(0, tForm * (1 - reverse));
        const effExpand = Math.max(0, tExpand * (1 - reverse * 0.5));

        ctx.fillStyle = 'rgba(2,3,10,0.28)';
        ctx.fillRect(0, 0, cw, ch);

        // City scale based on viewport
        const cityScale = Math.min(cw, ch) * 0.34;

        // === NUCLEUS (visible during react/expand & return) ===
        const nucleusAlpha = Math.max(0, 1 - effForm) * Math.max(0.4, 1 - reverse * 0.5 + (closing ? 0.5 : 0));
        if (nucleusAlpha > 0.01) {
          const baseR = 50 + tReact * 35 + effExpand * 60;
          const nucleusCY = cy - 60 * (1 - effExpand) + reverse * 30;
          const intensity = (0.6 + tReact * 0.4) * nucleusAlpha;

          // Outer halo
          const g1 = safeGradient(ctx, cx, nucleusCY, 0, cx, nucleusCY, baseR * 2.1);
          g1.addColorStop(0, `rgba(70,210,255,${safeAlpha(0.18 * intensity)})`);
          g1.addColorStop(0.4, `rgba(20,100,200,${safeAlpha(0.08 * intensity)})`);
          g1.addColorStop(1, 'rgba(0,0,0,0)');
          ctx.fillStyle = g1;
          ctx.fillRect(0, 0, cw, ch);

          // Core
          const coreR = safeRadius(18 + effExpand * 22, 1);
          const g2 = safeGradient(ctx, cx, nucleusCY, 0, cx, nucleusCY, coreR);
          g2.addColorStop(0, `rgba(230,250,255,${safeAlpha(0.6 * intensity)})`);
          g2.addColorStop(0.5, `rgba(70,210,255,${safeAlpha(0.25 * intensity)})`);
          g2.addColorStop(1, 'rgba(0,0,0,0)');
          ctx.fillStyle = g2;
          ctx.beginPath(); ctx.arc(cx, nucleusCY, coreR, 0, Math.PI * 2); ctx.fill();
        }

        // === PARTICLES (nucleus orbit -> city grid -> return) ===
        particles.forEach((p) => {
          p.phase += dt * p.speed;
          const nucleusCY = cy - 60 * (1 - effExpand) + reverse * 30;
          const wobble = Math.sin(p.phase * 2) * 6;
          const nx = cx + Math.cos(p.angle + elapsed * 0.45 * p.speed) * (p.dist + wobble);
          const ny = nucleusCY + Math.sin(p.angle + elapsed * 0.45 * p.speed) * (p.dist + wobble) * 0.85;

          // Grid position (in city plane)
          const [gPx, gPy] = isoProject(p.gridU * cityScale * 1.2, p.gridV * cityScale * 1.2, 0);
          const gridX = cx + gPx;
          const gridY = cy + gPy;

          // Blend nucleus → grid → nucleus
          const morph = Math.pow(effForm, 1.4);
          const px = nx * (1 - morph) + gridX * morph;
          const py = ny * (1 - morph) + gridY * morph;

          const pulse = 0.5 + Math.sin(elapsed * 2 + p.phase) * 0.5;
          const baseA = p.brightness * (0.4 + pulse * 0.6);
          const cityGlow = morph * 0.7;
          const alpha = safeAlpha((baseA * (1 - cityGlow * 0.35) + cityGlow * 0.55) * (1 - reverse * 0.6));

          ctx.fillStyle = `rgba(130,225,255,${alpha})`;
          ctx.beginPath();
          ctx.arc(px, py, safeRadius(p.size * (0.9 + pulse * 0.3), 0.3), 0, Math.PI * 2);
          ctx.fill();
        });

        // === ISOMETRIC HOLOGRAPHIC CITY ===
        if (effForm > 0.05) {
          const cityAlpha = Math.pow(effForm, 1.5);

          // Isometric tile floor
          const tilesU = ROAD_LINES;
          for (let i = -tilesU; i <= tilesU; i++) {
            const u1 = i / tilesU;
            const [x1a, y1a] = isoProject(u1 * cityScale, -cityScale, 0);
            const [x1b, y1b] = isoProject(u1 * cityScale, cityScale, 0);
            ctx.strokeStyle = `rgba(40,170,255,${safeAlpha(0.10 * cityAlpha)})`;
            ctx.lineWidth = 0.5;
            ctx.beginPath();
            ctx.moveTo(cx + x1a, cy + y1a);
            ctx.lineTo(cx + x1b, cy + y1b);
            ctx.stroke();
            // Perpendicular
            const [x2a, y2a] = isoProject(-cityScale, u1 * cityScale, 0);
            const [x2b, y2b] = isoProject(cityScale, u1 * cityScale, 0);
            ctx.beginPath();
            ctx.moveTo(cx + x2a, cy + y2a);
            ctx.lineTo(cx + x2b, cy + y2b);
            ctx.stroke();
          }

          // Bright main roads (avenues) — every 3rd grid
          for (let i = -tilesU; i <= tilesU; i += 3) {
            const u1 = i / tilesU;
            const [x1a, y1a] = isoProject(u1 * cityScale, -cityScale, 0);
            const [x1b, y1b] = isoProject(u1 * cityScale, cityScale, 0);
            ctx.strokeStyle = `rgba(80,210,255,${safeAlpha(0.30 * cityAlpha)})`;
            ctx.lineWidth = 1.2;
            ctx.beginPath();
            ctx.moveTo(cx + x1a, cy + y1a);
            ctx.lineTo(cx + x1b, cy + y1b);
            ctx.stroke();

            const [x2a, y2a] = isoProject(-cityScale, u1 * cityScale, 0);
            const [x2b, y2b] = isoProject(cityScale, u1 * cityScale, 0);
            ctx.beginPath();
            ctx.moveTo(cx + x2a, cy + y2a);
            ctx.lineTo(cx + x2b, cy + y2b);
            ctx.stroke();

            // Traveling light pulses on main roads (procedural traffic)
            const pulseProg = ((elapsed * 0.4 + (i / tilesU) * 0.3) % 1);
            const [px1, py1] = isoProject(u1 * cityScale, -cityScale + 2 * cityScale * pulseProg, 0);
            ctx.fillStyle = `rgba(150,235,255,${safeAlpha(0.7 * cityAlpha)})`;
            ctx.beginPath(); ctx.arc(cx + px1, cy + py1, 1.6, 0, Math.PI * 2); ctx.fill();
          }

          // Outer ground glow ring
          const groundR = cityScale * 1.2;
          const groundGrad = safeGradient(ctx, cx, cy, groundR * 0.4, cx, cy, groundR);
          groundGrad.addColorStop(0, `rgba(20,80,160,${safeAlpha(0.16 * cityAlpha)})`);
          groundGrad.addColorStop(1, 'rgba(0,0,0,0)');
          ctx.fillStyle = groundGrad;
          ctx.fillRect(cx - groundR, cy - groundR * 0.55, groundR * 2, groundR * 1.1);

          // Buildings (isometric boxes)
          buildings.forEach((b) => {
            const buildP = Math.max(0, Math.min((effForm - b.delay) * 1.6, 1));
            if (buildP <= 0) return;
            const hScale = cityScale * 0.9;
            const h = b.h * buildP * hScale;
            const gu = b.gu * cityScale;
            const gv = b.gv * cityScale;
            const w = b.w * cityScale;
            const d = b.d * cityScale;

            // Compute the 4 base corners
            const c1 = isoProject(gu - w / 2, gv - d / 2);
            const c2 = isoProject(gu + w / 2, gv - d / 2);
            const c3 = isoProject(gu + w / 2, gv + d / 2);
            const c4 = isoProject(gu - w / 2, gv + d / 2);
            // Top corners (raised)
            const t1 = [c1[0], c1[1] - h];
            const t2 = [c2[0], c2[1] - h];
            const t3 = [c3[0], c3[1] - h];
            const t4 = [c4[0], c4[1] - h];

            // Filled top face (translucent)
            ctx.fillStyle = `rgba(40,150,255,${safeAlpha(0.18 * buildP * cityAlpha)})`;
            ctx.beginPath();
            ctx.moveTo(cx + t1[0], cy + t1[1]);
            ctx.lineTo(cx + t2[0], cy + t2[1]);
            ctx.lineTo(cx + t3[0], cy + t3[1]);
            ctx.lineTo(cx + t4[0], cy + t4[1]);
            ctx.closePath();
            ctx.fill();

            // Right face
            ctx.fillStyle = `rgba(20,90,180,${safeAlpha(0.22 * buildP * cityAlpha)})`;
            ctx.beginPath();
            ctx.moveTo(cx + c2[0], cy + c2[1]);
            ctx.lineTo(cx + c3[0], cy + c3[1]);
            ctx.lineTo(cx + t3[0], cy + t3[1]);
            ctx.lineTo(cx + t2[0], cy + t2[1]);
            ctx.closePath();
            ctx.fill();

            // Left face
            ctx.fillStyle = `rgba(10,60,140,${safeAlpha(0.18 * buildP * cityAlpha)})`;
            ctx.beginPath();
            ctx.moveTo(cx + c3[0], cy + c3[1]);
            ctx.lineTo(cx + c4[0], cy + c4[1]);
            ctx.lineTo(cx + t4[0], cy + t4[1]);
            ctx.lineTo(cx + t3[0], cy + t3[1]);
            ctx.closePath();
            ctx.fill();

            // Wireframe edges
            ctx.strokeStyle = `rgba(90,210,255,${safeAlpha(0.5 * buildP * cityAlpha)})`;
            ctx.lineWidth = 0.7;
            // Top edges
            ctx.beginPath();
            ctx.moveTo(cx + t1[0], cy + t1[1]);
            ctx.lineTo(cx + t2[0], cy + t2[1]);
            ctx.lineTo(cx + t3[0], cy + t3[1]);
            ctx.lineTo(cx + t4[0], cy + t4[1]);
            ctx.closePath();
            ctx.stroke();
            // Vertical edges
            ctx.beginPath();
            ctx.moveTo(cx + c2[0], cy + c2[1]); ctx.lineTo(cx + t2[0], cy + t2[1]);
            ctx.moveTo(cx + c3[0], cy + c3[1]); ctx.lineTo(cx + t3[0], cy + t3[1]);
            ctx.moveTo(cx + c4[0], cy + c4[1]); ctx.lineTo(cx + t4[0], cy + t4[1]);
            ctx.stroke();

            // Windows (lights pulsing)
            const rowCount = Math.max(2, Math.floor(b.h * 7));
            for (let r = 1; r < rowCount; r++) {
              const wh = (r / rowCount) * h;
              const wp = Math.sin(elapsed * 1.6 + b.seed * 8 + r) * 0.4 + 0.6;
              ctx.fillStyle = `rgba(160,235,255,${safeAlpha(0.25 * wp * buildP * cityAlpha)})`;
              const wl1 = [c2[0], c2[1] - wh];
              const wl2 = [t2[0] + (t3[0] - t2[0]) * 0.3, t2[1] + (t3[1] - t2[1]) * 0.3 - 0];
              // Just draw small bars on the right face
              ctx.fillRect(cx + c2[0] + 1, cy + c2[1] - wh, Math.max(1, (c3[0] - c2[0]) * 0.7), 1.2);
            }
          });

          // === LOCATION PIN ===
          const pinReady = Math.max(0, Math.min((effForm - 0.35) * 2, 1)) * (1 - reverse);
          if (pinReady > 0) {
            const pinPulse = 1 + Math.sin(elapsed * 3) * 0.12;
            const pinX = cx;
            const pinY = cy - 40 * pinReady;

            // Ground halo rings (concentric pulses)
            for (let r = 0; r < 4; r++) {
              const rr = ((elapsed * 35 + r * 35) % 130);
              const ringA = (1 - rr / 130) * 0.35 * pinReady * cityAlpha;
              ctx.strokeStyle = `rgba(80,220,255,${safeAlpha(ringA)})`;
              ctx.lineWidth = 1;
              ctx.beginPath();
              ctx.ellipse(pinX, cy + 4, safeRadius(rr * 0.8, 1), safeRadius(rr * 0.26, 1), 0, 0, Math.PI * 2);
              ctx.stroke();
            }

            // Solid pin glow
            const pgR = safeRadius(46 * pinPulse * pinReady, 1);
            const pg = safeGradient(ctx, pinX, pinY + 10, 0, pinX, pinY + 10, pgR);
            pg.addColorStop(0, `rgba(80,220,255,${safeAlpha(0.6 * pinReady)})`);
            pg.addColorStop(1, 'rgba(0,0,0,0)');
            ctx.fillStyle = pg;
            ctx.beginPath(); ctx.arc(pinX, pinY + 10, pgR, 0, Math.PI * 2); ctx.fill();

            // Pin teardrop shape (filled + glow)
            const ps = 14 * pinReady;
            ctx.fillStyle = `rgba(130,230,255,${safeAlpha(0.95 * pinReady)})`;
            // Outer drop
            ctx.beginPath();
            ctx.moveTo(pinX, pinY - ps * 1.4);
            ctx.bezierCurveTo(pinX + ps * 1.1, pinY - ps * 1.2, pinX + ps * 1.1, pinY + ps * 0.2, pinX, pinY + ps * 1.0);
            ctx.bezierCurveTo(pinX - ps * 1.1, pinY + ps * 0.2, pinX - ps * 1.1, pinY - ps * 1.2, pinX, pinY - ps * 1.4);
            ctx.closePath();
            ctx.fill();

            // Inner circle (hole)
            ctx.fillStyle = `rgba(8,18,40,${safeAlpha(1 * pinReady)})`;
            ctx.beginPath(); ctx.arc(pinX, pinY - ps * 0.4, ps * 0.45, 0, Math.PI * 2); ctx.fill();

            // Highlight
            ctx.fillStyle = `rgba(255,255,255,${safeAlpha(0.7 * pinReady)})`;
            ctx.beginPath(); ctx.arc(pinX - ps * 0.18, pinY - ps * 0.8, ps * 0.12, 0, Math.PI * 2); ctx.fill();
          }
        }

        // === FRAME CORNERS ===
        const frameA = Math.min(elapsed * 0.6, 1) * (1 - reverse * 0.5);
        ctx.strokeStyle = `rgba(40,140,200,${safeAlpha(0.20 * frameA)})`;
        ctx.lineWidth = 1;
        const cl = 28;
        const corners = [
          [22, 22, cl, 0, 0, cl],
          [cw - 22, 22, -cl, 0, 0, cl],
          [22, ch - 22, cl, 0, 0, -cl],
          [cw - 22, ch - 22, -cl, 0, 0, -cl],
        ];
        corners.forEach(([x, y, dx, dy, dx2, dy2]) => {
          ctx.beginPath();
          ctx.moveTo(x + dx, y + dy);
          ctx.lineTo(x, y);
          ctx.lineTo(x + dx2, y + dy2);
          ctx.stroke();
        });
      } catch {
        // skip bad frame
      }
      animRef.current = requestAnimationFrame(draw);
    };

    animRef.current = requestAnimationFrame(draw);
    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen, closing]);

  // Format helpers
  const timeStr = useMemo(() =>
    now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Fortaleza' }),
    [now]);
  const dateStr = useMemo(() =>
    now.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long', timeZone: 'America/Fortaleza' }),
    [now]);

  const cityShort = locationData?.city || '—';
  const stateLabel = locationData?.state || '';
  const countryLabel = locationData?.country || '—';
  const cityFull = locationData?.city
    ? `${locationData.city}${stateLabel ? ` - ${stateLabel}` : ''}${countryLabel && countryLabel !== '—' ? `, ${countryLabel}` : ''}`
    : 'Detectando localização...';
  const tempLabel = locationData?.temperature != null ? `${Math.round(locationData.temperature)}°C` : '—';
  const latLabel = locationData?.lat != null ? locationData.lat.toFixed(4) : '—';
  const lonLabel = locationData?.lon != null ? locationData.lon.toFixed(4) : '—';
  const altitudeLabel = locationData?.altitude != null ? `${Math.round(locationData.altitude)} m` : '—';
  const accuracyLabel = locationData?.accuracy != null ? `${locationData.accuracy.toFixed(1)} m` : '—';
  const gpsActive = locationData?.lat != null && locationData?.source === 'device';

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: closing ? 0 : 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: closing ? PHASE_5_RETURN / 1000 : 0.6 }}
          className="fixed inset-0 z-50 bg-[#02030a]"
          data-testid="holographic-location"
        >
          <canvas
            ref={canvasRef}
            className="absolute inset-0 w-full h-full"
            style={{ width: '100%', height: '100%' }}
          />

          {/* TOP-LEFT: Identifier */}
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: phase >= 1 ? 1 : 0, x: phase >= 1 ? 0 : -10 }}
            transition={{ duration: 0.6 }}
            className="absolute top-5 left-6 z-10 pointer-events-none"
          >
            <div className="text-[10px] font-mono tracking-[0.4em] text-white/50 uppercase">Athena · Geo</div>
            <div className="mt-1 flex items-center gap-2">
              <div className={`w-1.5 h-1.5 rounded-full ${gpsActive ? 'bg-blue-400 animate-pulse' : 'bg-white/20'}`} />
              <div className="text-[10px] font-mono tracking-[0.2em] text-blue-300/70 uppercase">
                {phase < 2 ? 'Iniciando' : phase < 3 ? 'Renderizando' : 'Localização ativa'}
              </div>
            </div>
          </motion.div>

          {/* TOP-RIGHT: Close button */}
          <button
            onClick={handleClose}
            className="absolute top-5 right-6 z-20 p-2 rounded-full bg-white/[0.03] border border-white/5 hover:bg-white/[0.06] hover:border-blue-400/30 text-white/40 hover:text-white/80 transition-all"
            data-testid="close-location"
            aria-label="Fechar"
          >
            <X size={14} />
          </button>

          {/* TOP-CENTER: City title with clima */}
          <AnimatePresence>
            {phase >= 3 && locationData?.city && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.7, delay: 0.2 }}
                className="absolute top-5 left-1/2 -translate-x-1/2 z-10 pointer-events-none"
                data-testid="location-title"
              >
                <div className="px-5 py-3 rounded-xl bg-[#050a18]/75 backdrop-blur-md border border-blue-400/15 shadow-[0_0_40px_rgba(60,180,255,0.08)] text-center">
                  <p className="text-sm md:text-base font-light font-['Outfit'] tracking-[0.18em] text-white/95 uppercase">
                    {cityFull}
                  </p>
                  <div className="mt-2 flex items-center justify-center gap-3 text-[10px] text-white/45 font-mono">
                    <span className="flex items-center gap-1.5"><Clock size={11} weight="light" />{timeStr}</span>
                    <span className="opacity-30">·</span>
                    <span className="flex items-center gap-1.5"><Sun size={11} weight="light" />{tempLabel}</span>
                    <span className="opacity-30">·</span>
                    <span className="capitalize">{dateStr}</span>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* LEFT: Real-time data panel */}
          <AnimatePresence>
            {phase >= 3 && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.7, delay: 0.35 }}
                className="absolute left-6 top-1/2 -translate-y-1/2 z-10 pointer-events-none w-[210px]"
                data-testid="location-data-panel"
              >
                <div className="rounded-2xl bg-[#050a18]/70 backdrop-blur-md border border-blue-400/12 p-4 shadow-[0_0_40px_rgba(60,180,255,0.06)]">
                  <div className="text-[9px] font-mono tracking-[0.3em] text-blue-300/60 uppercase mb-3">Dados em tempo real</div>
                  <DataField label="Cidade" value={cityShort} />
                  <DataField label="País" value={countryLabel} />
                  <DataField label="Latitude" value={latLabel} mono />
                  <DataField label="Longitude" value={lonLabel} mono />
                  <DataField label="Precisão" value={accuracyLabel} mono />
                  <DataField label="Altitude" value={altitudeLabel} mono />
                  <DataField label="Horário local" value={timeStr} mono />
                  <DataField label="Temperatura" value={tempLabel} mono />
                  <div className="mt-3 pt-3 border-t border-white/[0.06]">
                    <div className="text-[9px] font-mono tracking-[0.3em] text-white/35 uppercase mb-1.5">Conexão</div>
                    <div className="flex items-center gap-2">
                      <span className={`w-1.5 h-1.5 rounded-full ${gpsActive ? 'bg-green-400 animate-pulse shadow-[0_0_6px_rgba(74,222,128,0.6)]' : 'bg-white/20'}`} />
                      <span className={`text-[11px] font-mono ${gpsActive ? 'text-green-300/90' : 'text-white/40'}`}>
                        {gpsActive ? 'GPS Ativo' : 'Aguardando GPS'}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* RIGHT: Actions panel */}
          <AnimatePresence>
            {phase >= 3 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.7, delay: 0.5 }}
                className="absolute right-6 top-1/2 -translate-y-1/2 z-10 pointer-events-none w-[180px]"
                data-testid="location-actions-panel"
              >
                <div className="space-y-2.5">
                  <ActionRow icon={<Cube size={13} weight="light" />} label="Visão 3D" value="Ativa" active />
                  <ActionRow icon={<MagnifyingGlass size={13} weight="light" />} label="Zoom" value={`${actions.zoom.toFixed(2)}x`} active />
                  <ActionRow icon={<Broadcast size={13} weight="light" />} label="Satélite" value="Desativado" />
                  <ActionRow icon={<Path size={13} weight="light" />} label="Rota" value="Desativada" />
                  <ActionRow icon={<Stack size={13} weight="light" />} label="Camadas" value="Ativas" active />
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* BOTTOM-RIGHT: Compass */}
          <AnimatePresence>
            {phase >= 3 && (
              <motion.div
                initial={{ opacity: 0, scale: 0.85 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.7, delay: 0.6 }}
                className="absolute right-6 bottom-24 z-10 pointer-events-none"
                data-testid="compass"
              >
                <Compass />
              </motion.div>
            )}
          </AnimatePresence>

          {/* BOTTOM-CENTER: Transcript bar */}
          <AnimatePresence>
            {phase >= 3 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.7, delay: 0.55 }}
                className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 pointer-events-none"
              >
                <div className="px-5 py-3 rounded-full bg-[#050a18]/75 backdrop-blur-md border border-blue-400/15 flex items-center gap-3 max-w-md">
                  <Waveform size={14} weight="light" className="text-blue-300/70 flex-shrink-0" />
                  <p className="text-[11px] font-light text-white/65 font-mono whitespace-nowrap">
                    Athena: "Você está em {cityShort}{stateLabel ? `, ${stateLabel}` : ''}{countryLabel && countryLabel !== '—' ? `, ${countryLabel}` : ''}."
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function DataField({ label, value, mono = false }) {
  return (
    <div className="mb-2">
      <div className="text-[9px] font-mono tracking-[0.25em] text-white/35 uppercase">{label}</div>
      <div className={`text-[12px] text-white/90 truncate ${mono ? 'font-mono' : 'font-light'}`}>{value}</div>
    </div>
  );
}

function ActionRow({ icon, label, value, active = false }) {
  return (
    <div className={`flex items-center gap-3 px-3 py-2.5 rounded-lg bg-[#050a18]/70 backdrop-blur-md border ${active ? 'border-blue-400/25' : 'border-white/5'}`}>
      <div className={`flex-shrink-0 ${active ? 'text-blue-300' : 'text-white/35'}`}>{icon}</div>
      <div className="min-w-0 flex-1">
        <div className="text-[9px] font-mono uppercase tracking-[0.22em] text-white/40">{label}</div>
        <div className={`text-[11px] font-light truncate ${active ? 'text-blue-200/95' : 'text-white/55'}`}>{value}</div>
      </div>
    </div>
  );
}

function Compass() {
  return (
    <div className="relative w-[80px] h-[80px] rounded-full bg-[#050a18]/75 backdrop-blur-md border border-blue-400/20 flex items-center justify-center shadow-[0_0_30px_rgba(60,180,255,0.10)]">
      {/* Cardinal points */}
      <span className="absolute top-1.5 left-1/2 -translate-x-1/2 text-[9px] font-mono text-blue-300/80">N</span>
      <span className="absolute bottom-1.5 left-1/2 -translate-x-1/2 text-[9px] font-mono text-white/40">S</span>
      <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-[9px] font-mono text-white/40">W</span>
      <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[9px] font-mono text-white/40">E</span>

      {/* Needle */}
      <svg width="38" height="38" viewBox="0 0 38 38" className="relative">
        <polygon points="19,5 22,19 19,17 16,19" fill="#ff6464" />
        <polygon points="19,33 22,19 19,21 16,19" fill="#6e7aa6" />
        <circle cx="19" cy="19" r="1.6" fill="#bcd6ff" />
      </svg>
    </div>
  );
}
